import type { AppRouter } from "@postdaily/api/routers/index";
import { env } from "@postdaily/env/web";
import { TooltipProvider } from "@postdaily/ui/components/tooltip";
import { PRELOADED_FONT_URLS } from "@postdaily/ui/lib/fonts";
import type { QueryClient } from "@tanstack/react-query";
// Devtools are intentionally disabled below; re-add these imports when enabling.
// import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import {
  createRootRouteWithContext,
  HeadContent,
  Outlet,
  Scripts,
} from "@tanstack/react-router";
// import { TanStackRouterDevtools } from "@tanstack/react-router-devtools";
import type { TRPCOptionsProxy } from "@trpc/tanstack-react-query";
import { NuqsAdapter } from "nuqs/adapters/tanstack-router";
import { lazy, Suspense } from "react";
import { AnalyticsProvider } from "@/components/analytics/posthog-provider";
import { NotFoundPage } from "@/components/marketing/not-found-page";
import { ThemeProvider } from "@/components/theme-provider";
import { rootJsonLdScript, rootSeoLinks, SITE } from "@/lib/seo";
import appCss from "../index.css?url";

/**
 * Toasts are only ever raised by a click (sign out, a form result), never during
 * load, so sonner does not belong in the entry chunk. React starts fetching this
 * on first render, so the Toaster is mounted long before anything can trigger it.
 */
const ToasterLazy = lazy(() =>
  import("@postdaily/ui/components/sonner").then((m) => ({
    default: m.Toaster,
  })),
);

/**
 * The cookie banner and preferences dialog, for every route.
 *
 * Lazy behind a `null` fallback for the same reason as the Toaster, plus one
 * that matters more here: marketing pages are prerendered to static HTML with
 * `failOnError: true`, so this must contribute nothing to the server render and
 * have nothing to mismatch on hydration.
 *
 * It sits beside `<Outlet />` rather than inside a layout because it has to
 * cover both the marketing site and the signed-in app — the app is where
 * analytics identifies people by name and email, which is precisely what needs
 * asking about.
 */
const CookieConsentLazy = lazy(() =>
  import("@/components/cookie-consent/cookie-consent").then((m) => ({
    default: m.CookieConsent,
  })),
);

/**
 * Runs before first paint to apply the persisted/system color mode, preventing
 * a flash of the wrong theme on SSR hydration. Mirrors next-themes' own logic
 * (storageKey "theme"); next-themes takes over for runtime changes once mounted.
 */
const themeInitScript = `(function(){try{var s=localStorage.getItem("theme");var d=window.matchMedia("(prefers-color-scheme: dark)").matches;var t=document.documentElement;if(s==="dark"||((s==="system"||!s)&&d)){t.classList.add("dark");t.style.colorScheme="dark";}else{t.classList.remove("dark");t.style.colorScheme="light";}}catch(e){}})();`;
export interface RouterAppContext {
  trpc: TRPCOptionsProxy<AppRouter>;
  queryClient: QueryClient;
}

export const Route = createRootRouteWithContext<RouterAppContext>()({
  head: () => ({
    meta: [
      {
        charSet: "utf-8",
      },
      {
        name: "viewport",
        content: "width=device-width, initial-scale=1",
      },
      {
        title: SITE.defaultTitle,
      },
      {
        name: "description",
        content: SITE.defaultDescription,
      },
    ],
    links: [
      // Warm the API origin: the header's session check and the pricing
      // section both call it, and without this each pays a cold DNS + TLS
      // handshake at the moment it fires.
      {
        rel: "preconnect",
        href: new URL(env.VITE_SERVER_URL).origin,
        crossOrigin: "anonymous",
      },
      /*
       * The two faces the first screen paints in. They are only discoverable
       * once the stylesheet has parsed, which puts them a round trip late — and
       * the LCP element here is hero TEXT, so that delay lands directly on LCP.
       * Measured: dropping these preloads costs ~3.4s of LCP on a throttled
       * phone. They belong in front of the stylesheet, not behind it.
       */
      ...PRELOADED_FONT_URLS.map((href) => ({
        rel: "preload",
        as: "font",
        type: "font/woff2",
        href,
        crossOrigin: "anonymous",
      })),
      {
        rel: "stylesheet",
        href: appCss,
      },
      ...rootSeoLinks(),
    ],
    scripts: [rootJsonLdScript()],
  }),

  component: RootDocument,
  notFoundComponent: NotFoundPage,
});

function RootDocument() {
  return (
    <html lang="en" suppressHydrationWarning>
      <head>
        {/* biome-ignore lint/security/noDangerouslySetInnerHtml: static no-FOUC theme bootstrap, no user input */}
        <script dangerouslySetInnerHTML={{ __html: themeInitScript }} />
        <HeadContent />
      </head>
      <body suppressHydrationWarning>
        <AnalyticsProvider>
          <ThemeProvider>
            <TooltipProvider>
              <NuqsAdapter>
                <Outlet />
              </NuqsAdapter>
            </TooltipProvider>
            <Suspense fallback={null}>
              <ToasterLazy richColors closeButton />
            </Suspense>
            <Suspense fallback={null}>
              <CookieConsentLazy />
            </Suspense>
          </ThemeProvider>
        </AnalyticsProvider>
        {/* <TanStackRouterDevtools position="bottom-left" />
        <ReactQueryDevtools position="bottom" buttonPosition="bottom-right" /> */}
        <Scripts />
      </body>
    </html>
  );
}
